/**
 * Two profile holders on the new design (E3b-2a step 5): nothing is recorded
 * until both have tapped (Mark, 2026-10-21). The scan (or nearby match) only
 * replaces finding each other: each phone raises its own sheet on its own code
 * page, holds its choice, and taps the server's offer (exchangeOffer, 055). The
 * second tap forms it — the server writes the room record with both seats —
 * and only then does each phone write its own side (pxpSides), once.
 *
 * Each side builds an `offer` for its code page:
 *   { role, room, me, peer: {profileUserId, name, photoUrl, photoMeta?, colour?},
 *     repeat, existingRoom, via: 'scan'|'nearby', tapArgs, form(answer) }
 * - `room` is the host's QR room (the offer's key); `existingRoom` the room
 *   the two already share, as this phone knows it.
 * - `tapArgs` ride this phone's tap — the host's carry its meeting verdict.
 * - `form(answer)` writes this phone's side once the answer is `formed` and
 *   resolves to the room to land on.
 *
 * The old design (flag off) never comes here: qrPXPExchange's flow, unchanged.
 */

import { upsertConnectionView } from '../../services/storage/userDataStorage';
import { logFunnelQRScanned, logConnectionLocation } from '../../services/analytics/eventLogger';
import { handOffNearbyTiming } from '../../services/proximity/nearbyTiming';
import { MEETING_KIND } from '../../services/meetings/meetingRule';
import { setReconnectPin } from '../../services/people/reconnectPin';
import { openOffer } from '../../services/exchange/exchangeOffer';
import {
  decideHostSide,
  writeHostRecord,
  sendHostReply,
  replyMeetingOf,
  writeGuestRecord,
  sendKnock,
  hostCardFromLanding,
} from './pxpSides';

const viaOf = (trigger) => (trigger === 'proximity' ? 'nearby' : 'scan');

/**
 * The host, on a knock: decide first-time / reconnect / re-meet read-only and
 * raise the sheet. Writes nothing.
 *
 * @param {Object} payload - the profile_exchange knock
 * @param {Object} config - QRScreen's (chatRoomId, profileUserId, refs, context methods)
 * @returns {Promise<Object>} the offer
 */
export const prepareHostOffer = async (payload, config) => {
  const { chatRoomId, profileUserId } = config;
  const { existingChatRoomId, verdict, pxpMetadata, pxpTimestamp } = await decideHostSide(
    payload,
    config
  );
  const isReconnection = !!existingChatRoomId;
  const peer = {
    profileUserId: payload.senderProfileUserId,
    name: payload.profileData?.name || '',
    photoUrl: payload.profileData?.photoUrl || null,
  };
  return {
    role: 'host',
    room: chatRoomId,
    me: profileUserId,
    peer,
    repeat: isReconnection,
    existingRoom: existingChatRoomId,
    via: viaOf(payload.trigger),
    tapArgs: { meeting: replyMeetingOf(verdict, pxpMetadata), existingRoom: existingChatRoomId },
    form: () =>
      formHostSide({
        ...config,
        payload,
        existingChatRoomId,
        verdict,
        pxpMetadata,
        pxpTimestamp,
      }),
  };
};

/** The host's side, once both have tapped. Resolves to the room to land on. */
const formHostSide = async ({
  payload,
  existingChatRoomId,
  verdict,
  pxpMetadata,
  pxpTimestamp,
  chatRoomId,
  profileUserId,
  profileRef,
  locationDataRef,
  locationPromiseRef,
  updateAnonData,
  updateConnectionMetadata,
  updateSessionChatRoomId,
}) => {
  const isReconnection = !!existingChatRoomId;
  const targetChatRoomId = isReconnection ? existingChatRoomId : chatRoomId;
  const freshProfileData = payload.profileData
    ? { ...payload.profileData, senderProfileUserId: payload.senderProfileUserId }
    : null;
  await writeHostRecord({
    isReconnection,
    verdict,
    chatRoomId,
    targetChatRoomId,
    profileUserId,
    freshProfileData,
    pxpMetadata,
    pxpTimestamp,
    trigger: payload.trigger,
    updateAnonData,
    updateConnectionMetadata,
    locationDataRef,
    locationPromiseRef,
  });
  // The reply is the persisted record of what the host auto-shares (first name
  // + photo) and the verdict — sent now, when the scanner can read the room
  const currentProfile = await sendHostReply({
    profileRef,
    isReconnection,
    chatRoomId,
    targetChatRoomId,
    profileUserId,
    verdict,
    pxpMetadata,
  });
  if (!isReconnection) {
    upsertConnectionView(chatRoomId, profileUserId, { metadata: pxpMetadata });
    logFunnelQRScanned(chatRoomId, currentProfile?.name, payload.trigger);
    if (locationDataRef.current) {
      logConnectionLocation(chatRoomId, locationDataRef.current, 'radar');
    }
  }
  updateSessionChatRoomId(targetChatRoomId, true);
  if (payload.trigger === 'proximity') handOffNearbyTiming(targetChatRoomId, currentProfile?.name);
  if (verdict.kind === MEETING_KIND.RECONNECT) setReconnectPin(targetChatRoomId);
  return targetChatRoomId;
};

/**
 * The scanner, holding the claim on the host's room: open the offer, knock,
 * and raise the sheet on this phone's own code page. Writes nothing but the
 * knock (the host learns who scanned — visible only to the two of them).
 *
 * @throws when the server won't open the offer (this phone is busy elsewhere)
 */
export const startGuestOffer = async (config) => {
  const {
    hostProfileUserId,
    session,
    existingChatRoomId,
    profileUserId,
    profileRef,
    trigger,
    onOffer,
  } = config;
  const room = session.chat_room_id;
  const opened = await openOffer(room, profileUserId);
  if (!opened || opened.state === 'over') {
    throw new Error(`offer not opened: ${opened?.reason || 'no answer'}`);
  }
  await sendKnock({
    hostChatRoomId: room,
    profileUserId,
    profileRef,
    isReconnection: !!existingChatRoomId,
    trigger,
  });
  onOffer({
    role: 'guest',
    room,
    me: profileUserId,
    peer: {
      profileUserId: hostProfileUserId,
      name: session.name || '',
      photoUrl: session.photoUrl || null,
      photoMeta: session.photoMeta || null,
      colour: session.colour || null,
    },
    repeat: !!existingChatRoomId,
    existingRoom: existingChatRoomId,
    via: viaOf(trigger),
    tapArgs: {},
    form: (answer) => formGuestSide({ ...config, room, answer }),
  });
};

/** The scanner's side, once both have tapped: adopt the host's verdict. */
const formGuestSide = async ({
  room,
  answer,
  session,
  hostProfileUserId,
  profileUserId,
  profileRef,
  trigger,
  locationDataRef,
  locationPromiseRef,
  updateAnonData,
  updateConnectionMetadata,
  updateSessionChatRoomId,
}) => {
  // The host decided the meeting and whether it's a repeat; the server carries both
  const isReconnection = !!answer.existingRoom;
  const targetChatRoomId = answer.existingRoom || room;
  const meeting = answer.meeting || {
    kind: isReconnection ? MEETING_KIND.RECONNECT : MEETING_KIND.FIRST,
  };
  const guestMetadata = await writeGuestRecord({
    meeting,
    isReconnection,
    targetChatRoomId,
    hostChatRoomId: room,
    profileUserId,
    freshProfileData: hostCardFromLanding(session, hostProfileUserId),
    locationDataRef,
    locationPromiseRef,
    updateAnonData,
    updateConnectionMetadata,
  });
  // The room record (both seats) is the server's, written at the second tap
  if (!isReconnection) upsertConnectionView(room, profileUserId, { metadata: guestMetadata });
  updateSessionChatRoomId(targetChatRoomId, true);
  if (trigger === 'proximity') handOffNearbyTiming(targetChatRoomId, profileRef.current?.name);
  if (meeting.kind === MEETING_KIND.RECONNECT) setReconnectPin(targetChatRoomId);
  return targetChatRoomId;
};
